import { bump, motionOk } from "./kit";

/**
 * Fly-to-HUD sprites (art wow pass, ui.md F4): coins from a kill arc up into the points pill, chest loot into the hub
 * resource pills. Earning should *travel* to owning — without it a +240 gold just appears.
 *
 * One pool of 12 sprites for the whole app (a kill burst never creates DOM). Each sprite is two nested elements so the
 * arc needs no path maths: the outer one moves X on a gentle ease, the inner one moves Y on an ease with a negative
 * first control point, so a coin first pops *away* from the HUD (down, 20–30 px) and then accelerates up into it —
 * a quadratic-looking arc from two 1-D tweens. Transform/opacity only; `will-change` is set while a sprite flies and
 * cleared when it lands, so at most 12 layers are ever promoted.
 */
const POOL = 12;
const FLIGHT_MS = 450;
const STAGGER_MS = 40;
/** Minimum gap between two bursts: at ×2 speed a wave of kills would otherwise keep the pool permanently busy. */
const BURST_GAP_MS = 150;

interface Sprite { outer: HTMLDivElement; inner: HTMLDivElement; busy: boolean }
let pool: Sprite[] | null = null;
let lastBurst = -Infinity;

function sprites(): Sprite[] {
  if (pool) return pool;
  const layer = document.createElement("div");
  layer.className = "flylayer";
  document.body.append(layer);
  pool = Array.from({ length: POOL }, () => {
    const outer = document.createElement("div");
    outer.className = "fly hidden";
    const inner = document.createElement("div");
    inner.className = "fly-in";
    outer.append(inner);
    layer.append(outer);
    return { outer, inner, busy: false };
  });
  return pool;
}

/** What a sprite looks like: the CSS coin, or a painted icon URL (the hub resource renders). */
export type FlyLook = { coin: true } | { img: string };

export interface FlyOptions {
  /** Called as each sprite lands (e.g. to bump the target pill). */
  onArrive?: (i: number) => void;
  /** Called once after the last sprite lands. */
  onDone?: () => void;
  /** Sprite size in px (18 for coins, 26 for resource icons). */
  size?: number;
  /** Ignore the burst throttle (the chest loot flight is one deliberate burst, not a kill stream). */
  force?: boolean;
}

/**
 * Flies up to `n` sprites (capped by what the pool has free) from a screen point to the centre of `target`. Returns how
 * many flew; 0 when throttled, when reduced motion is on, or when the target isn't laid out — callers still update
 * their numbers, the flight is decoration.
 */
export function flyTo(fromX: number, fromY: number, target: Element | null, n: number, look: FlyLook = { coin: true }, opts: FlyOptions = {}): number {
  const done = () => opts.onDone?.();
  if (!target || n <= 0 || !motionOk() || typeof document.body.animate !== "function") { done(); return 0; }
  const now = performance.now();
  if (!opts.force && now - lastBurst < BURST_GAP_MS) { done(); return 0; }
  const r = target.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) { done(); return 0; }
  lastBurst = now;
  const tx = r.left + r.width * 0.3, ty = r.top + r.height / 2;     // the pill's icon disc sits at its left
  const size = opts.size ?? 18;
  const free = sprites().filter((s) => !s.busy).slice(0, n);
  if (free.length === 0) { done(); return 0; }
  let landed = 0;
  free.forEach((s, i) => {
    s.busy = true;
    s.inner.className = `fly-in ${"coin" in look ? "coin" : "img"}`;
    s.inner.style.backgroundImage = "img" in look ? `url("${look.img}")` : "";
    s.inner.style.width = s.inner.style.height = `${size}px`;
    s.outer.classList.remove("hidden");
    s.outer.style.willChange = s.inner.style.willChange = "transform";
    // A little scatter at the start so a burst of 5 reads as 5 coins, not one thick one.
    const sx = fromX + (Math.random() - 0.5) * 26, sy = fromY + (Math.random() - 0.5) * 14;
    const delay = i * STAGGER_MS;
    const half = size / 2;
    const common: KeyframeAnimationOptions = { duration: FLIGHT_MS, delay, fill: "both" };
    s.outer.animate([{ transform: `translateX(${sx - half}px)` }, { transform: `translateX(${tx - half}px)` }], { ...common, easing: "cubic-bezier(.3,0,.6,1)" });
    const y = s.inner.animate([
      { transform: `translateY(${sy - half}px) scale(.6)`, opacity: 0 },
      { transform: `translateY(${sy - half + 6}px) scale(1.1)`, opacity: 1, offset: 0.12 },
      { transform: `translateY(${ty - half}px) scale(.8)`, opacity: 1 },
    ], { ...common, easing: "cubic-bezier(.55,-0.45,.85,.6)" });
    y.onfinish = () => {
      s.busy = false;
      s.outer.classList.add("hidden");
      s.outer.style.willChange = s.inner.style.willChange = "";
      s.outer.getAnimations().forEach((a) => a.cancel());
      y.cancel();
      opts.onArrive?.(i);
      if (++landed === free.length) done();
    };
  });
  return free.length;
}

/** Fly coins into a pill and pop it as each lands. */
export function flyCoinsTo(fromX: number, fromY: number, pill: HTMLElement | null, n: number, onDone?: () => void, force = false): number {
  return flyTo(fromX, fromY, pill, n, { coin: true }, { onArrive: () => bump(pill, 1.14, 160), onDone, force });
}
